import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import ContentEditor from "aldoo-ra/CMS/content-editor"
import { showNotification } from "aldoo-ra/Notification"
import { GetParams, GetParam, CreateParam, UpdateParam } from "../../ParamIO/api"
import NewsletterSettingsContentType from "aldoo-ra/CMSModels/NewsletterSettings"
import PermissionCheck from "aldoo-ra/PermissionChecker"

const NewsletterSettings = () => {
  const [fieldValues, setFieldValues] = useState({})
  const [loading, setLoading] = useState(true)
  const [originalValues, setOriginalValues] = useState({})
  const [isInitialized, setIsInitialized] = useState(false)

  // Load current settings from paramIO
  const loadCurrentSettings = async () => {
    try {
      const settings = {
        // Initialize with default values for all expected fields
        "MAX_BATCH_SIZE": 5000,
        "SPARKPOST_WEBHOOK_USERNAME": "",
        "SPARKPOST_WEBHOOK_PASSWORD": ""
      }

      // Load all newsletter settings
      const paramKeys = [
        "Newsletter.MAX_BATCH_SIZE",
        "Newsletter.SPARKPOST_WEBHOOK_USERNAME",
        "Newsletter.SPARKPOST_WEBHOOK_PASSWORD"
      ]

      for (const key of paramKeys) {
        try {
          const param = await GetParams({
            match: { address: key }
          }).catch(() => null)

          if (param?.data?.[0]) {
            const fieldKey = key.replace('Newsletter.', '')
            let value = param.data[0].value

            console.log(`Loading ${fieldKey}:`, value, typeof value)

            // Convert "none" back to default values
            if (value === "none" || value === null || value === undefined) {
              if (fieldKey === "MAX_BATCH_SIZE") value = 5000
              if (fieldKey === "SPARKPOST_WEBHOOK_USERNAME") value = ""
              if (fieldKey === "SPARKPOST_WEBHOOK_PASSWORD") value = ""
            }

            // Ensure numeric fields are actually numbers
            if (fieldKey === "MAX_BATCH_SIZE") {
              value = Number(value)
            }

            // String fields (webhook username and password) - ensure they're strings
            if (fieldKey === "SPARKPOST_WEBHOOK_USERNAME" || fieldKey === "SPARKPOST_WEBHOOK_PASSWORD") {
              value = String(value || "")
            }

            settings[fieldKey] = value
          }
        } catch (error) {
          console.warn(`Failed to load ${key}:`, error)
        }
      }

      return settings
    } catch (error) {
      console.error("Failed to load current settings:", error)
      // Return initialized object even on error
      return {
        "MAX_BATCH_SIZE": 5000,
        "SPARKPOST_WEBHOOK_USERNAME": "",
        "SPARKPOST_WEBHOOK_PASSWORD": ""
      }
    }
  }

  // Save settings to paramIO (only changed values)
  const handleSave = async (values) => {
    try {
      console.log('handleSave called with values:', values)
      console.log('originalValues:', originalValues)
      console.log('isInitialized:', isInitialized)

      // Don't save during initial load
      if (!isInitialized) {
        console.log('Skipping save: not initialized yet')
        return false
      }

      const savePromises = []

      // Compare with original values and only save what changed
      Object.entries(values).forEach(([fieldId, value]) => {
        // Skip if value is undefined or null (but allow 0, false, and empty string for valid cases)
        if (value === undefined || value === null) {
          console.log(`Skipping ${fieldId}: value is ${value}`)
          return
        }

        // For number fields, ensure value is actually a number
        if (fieldId === "MAX_BATCH_SIZE") {
          if (typeof value !== 'number' || isNaN(value)) {
            console.log(`Skipping ${fieldId}: invalid number value`, value)
            return
          }
        }

        const originalValue = originalValues[fieldId]
        const hasChanged = value !== originalValue

        console.log(`Field ${fieldId}: current=${value} (${typeof value}), original=${originalValue} (${typeof originalValue}), changed=${hasChanged}`)

        if (hasChanged) {
          const address = `Newsletter.${fieldId}`
          const description = getDescriptionForField(fieldId)

          console.log(`Saving ${address} with value:`, value, typeof value)

          // Check if parameter exists first, then choose Create vs Update
          const savePromise = GetParam(address).then(existing => {
            const paramData = {
              address,
              value: value,
              description
            }

            if (existing && existing.result !== false) {
              console.log(`Updating param ${address}:`, paramData)
              return UpdateParam(paramData)
            } else {
              console.log(`Creating param ${address}:`, paramData)
              return CreateParam(paramData)
            }
          }).catch(getError => {
            // If GetParam fails, assume parameter doesn't exist and create it
            console.log(`GetParam failed for ${address}, creating:`, { address, value, description })
            return CreateParam({
              address,
              value: value,
              description
            })
          })

          savePromises.push(savePromise)
        }
      })

      if (savePromises.length === 0) {
        console.log('No changes detected, skipping save')
        return false  // Return false to indicate no save was performed
      }

      console.log(`Saving ${savePromises.length} parameter(s)...`)
      const results = await Promise.all(savePromises)
      console.log('Save results:', results)

      // Update both original values and field values to reflect saved state
      setOriginalValues({ ...values })
      setFieldValues({ ...values })

      showNotification({
        message: `Newsletter settings saved successfully (${savePromises.length} changes)`,
        className: "bg-admin_success text-admin_text_inv",
      })

      console.log('Save completed successfully')
      return true
    } catch (error) {
      console.error("Failed to save newsletter settings:", error)
      showNotification({
        message: "Failed to save newsletter settings",
        className: "bg-admin_error text-admin_text_inv",
      })
      return false
    }
  }

  const getDescriptionForField = (fieldId) => {
    const descriptions = {
      "MAX_BATCH_SIZE": "Maximum recipients per SparkPost transmission. SparkPost allows up to 10,000 per batch. Lower values provide more frequent progress updates but slower overall sending.",
      "SPARKPOST_WEBHOOK_USERNAME": "Basic auth username for SparkPost webhook authentication. Leave empty to disable authentication (development only).",
      "SPARKPOST_WEBHOOK_PASSWORD": "Basic auth password for SparkPost webhook authentication. Leave empty to disable authentication (development only)."
    }
    return descriptions[fieldId] || "Newsletter configuration setting"
  }

  // Initialize the component
  useEffect(() => {
    const init = async () => {
      setLoading(true)
      setIsInitialized(false)
      try {
        const currentSettings = await loadCurrentSettings()

        setFieldValues(currentSettings)
        setOriginalValues(currentSettings)

        // Mark as initialized after a short delay to ensure ContentEditor has processed the values
        setTimeout(() => {
          setIsInitialized(true)
          console.log('Settings initialized, auto-save now enabled')
        }, 1000)
      } catch (error) {
        console.error("Failed to initialize newsletter settings:", error)
        showNotification({
          message: "Failed to load newsletter settings",
          className: "bg-admin_error text-admin_text_inv",
        })
      } finally {
        setLoading(false)
      }
    }

    init()
  }, [])

  if (loading) {
    return (
      <div className="p-6">
        <Typography variant="admin_body">Loading newsletter settings...</Typography>
      </div>
    )
  }


  return (
    <div className="p-6">
      <PermissionCheck permission="role:newsletter" />
      <div className="max-w-6xl mx-auto">
        <div className="bg-paper rounded-lg border border-admin_bordered">
          <ContentEditor
            title="Newsletter Configuration"
            contentType={NewsletterSettingsContentType}
            dataItem={{ content: fieldValues }}
            fieldValues={fieldValues}
            onFieldChange={(fieldId, value) => {
              console.log('Field changed:', fieldId, '=', value, typeof value)
            }}
            onSave={handleSave}
            onError={(errors) => {
              showNotification({
                message: Object.values(errors).find((e) => e) || "Validation error",
                className: "bg-admin_error text-admin_text_inv",
              })
            }}
            variant="default"
            showCopyPaste={false}
            showExport={false}
            autoSave={true}
            autoSaveDelay={500}
          />
        </div>
      </div>
    </div>
  )
}

export default NewsletterSettings